<template>
  <div id="keyword-search">
    <div class="page-header">
      <h2 class="page-title">키워드 검색</h2>
      <p class="page-desc">
        키워드 하나를 넣으면 기본정보·트렌드·성향·연관 키워드를 탭으로 나눠 봅니다.
        <strong>포화도가 낮을수록</strong> 경쟁 대비 노출에 유리합니다.
      </p>
    </div>

    <!-- 결과가 있으면 검색창을 접고, 무엇을 조회한 결과인지 크게 보여준다.
         입력만 바꾸고 조회를 안 했을 때 다른 키워드 결과로 오해하는 것을 막는다. -->
    <section v-if="searchedKeyword" class="result-head">
      <div class="rh-left">
        <div class="rh-label">분석 결과</div>
        <div class="rh-keyword">{{ searchedKeyword }}</div>
      </div>
      <div class="rh-right">
        <input
          v-model="form.seed" class="rh-input" type="text"
          placeholder="다른 키워드" maxlength="100" @keyup.enter="doSearch"
        />
        <button class="btn-search sm" :disabled="anyLoading" @click="doSearch">
          {{ anyLoading ? "조회 중…" : "다시 찾기" }}
        </button>
      </div>
    </section>
    <div v-if="searchedKeyword && isDirty" class="notice notice-warn">
      입력한 <b>「{{ form.seed.trim() }}」</b>는 아직 조회하지 않았습니다.
      아래 결과는 여전히 <b>「{{ searchedKeyword }}」</b>의 분석입니다 — <b>다시 찾기</b>를 눌러 주세요.
    </div>

    <section v-if="!searchedKeyword" class="search-card">
      <div class="seed-row">
        <input
          v-model="form.seed" class="seed-input" type="text"
          placeholder="예: 여의도맛집" maxlength="100" @keyup.enter="doSearch"
        />
        <button class="btn-search" :disabled="anyLoading" @click="doSearch">
          {{ anyLoading ? "조회 중…" : "찾기" }}
        </button>
      </div>
      <button class="btn-toggle-filter" @click="filterOpen = !filterOpen">
        {{ filterOpen ? "상세 조건 접기" : "상세 조건" }}
      </button>
      <div v-show="filterOpen" class="filter-row">
        <label><span>최소 검색량</span>
          <input v-model.number="form.minVolume" type="number" min="0" placeholder="예: 100" /></label>
        <label><span>최대 검색량</span>
          <input v-model.number="form.maxVolume" type="number" min="0" placeholder="예: 50000" /></label>
        <label><span>분석 개수</span>
          <input v-model.number="form.limit" type="number" min="1" max="40" placeholder="40" />
          <em class="hint">발행량을 조회할 상위 N개 (최대 40)</em></label>
      </div>
    </section>

    <!-- 최근 조회 — 다시 누르면 네이버를 부르지 않고 저장해 둔 결과를 그대로 보여 준다 -->
    <div v-if="history.length" class="history-bar">
      <span class="hb-label">최근 조회</span>
      <div class="hb-list">
        <span v-for="h in history" :key="h.id" class="hb-item"
              :class="{ active: h.id === activeHistoryId, pending: h.pending }">
          <button class="hb-open" :title="h.pending ? '조회가 아직 끝나지 않았습니다' : rel(h.at) + ' 조회'"
                  @click="restore(h.id)">
            {{ h.seed }}<em>{{ h.pending ? "조회 중…" : rel(h.at) }}</em>
          </button>
          <button class="hb-del" title="이 이력과 결과를 지웁니다" @click="dropHistory(h.id)">✕</button>
        </span>
      </div>
      <button class="hb-clear" @click="clearHistory">전체 삭제</button>
    </div>

    <div v-if="errorMessage" class="notice notice-error">{{ errorMessage }}</div>

    <!-- ===== 탭 ===== -->
    <template v-if="profile || profileLoading">
      <nav class="tab-bar">
        <button
          v-for="t in tabs" :key="t.key"
          class="tab" :class="{ active: activeTab === t.key }"
          @click="activeTab = t.key"
        >
          {{ t.label }}
          <span v-if="t.badge" class="tab-badge">{{ t.badge }}</span>
          <i v-else-if="t.key === 'related' && relatedLoading" class="tab-spin"></i>
        </button>
      </nav>

      <!-- ── 기본정보 ── -->
      <div v-show="activeTab === 'basic'" class="tab-panel">
        <div v-if="profileLoading" class="skeleton-card">기본정보 불러오는 중…</div>
        <template v-else-if="profile">
          <div v-if="profile.notice" class="notice notice-warn">{{ profile.notice }}</div>
          <div class="verdict" :class="verdict.tone">
            <div class="verdict-title">{{ verdict.title }}</div>
            <div class="verdict-desc">{{ verdict.desc }}</div>
          </div>

          <div class="metric-grid">
            <div class="metric">
              <div class="metric-label">월간 검색량</div>
              <div class="metric-value">{{ profile.totalVolume.toLocaleString() }}</div>
              <div class="metric-sub">{{ volumeGrade }}</div>
            </div>
            <div class="metric">
              <div class="metric-label">월간 발행량</div>
              <div class="metric-value">{{ fmt(profile.docCount) }}</div>
              <div class="metric-sub">블로그 문서 수</div>
            </div>
            <div class="metric">
              <div class="metric-label">포화 지수</div>
              <div class="metric-value" :class="saturationClass(profile.saturation)">
                {{ profile.saturation === null ? "-" : profile.saturation.toFixed(2) }}
              </div>
              <div class="metric-sub">{{ saturationText }}</div>
            </div>
            <div class="metric">
              <div class="metric-label">경쟁정도</div>
              <div class="metric-value sm">{{ profile.competition || "-" }}</div>
              <div class="metric-sub">네이버 광고 기준</div>
            </div>
          </div>

          <!-- PC vs 모바일 -->
          <div class="detail-card">
            <div class="dc-title">기기 분포</div>
            <div class="device-bar">
              <!-- 한쪽 비중이 아주 작으면(모바일 97% 같은 경우) 그 칸에 글씨가 안 들어가
                   반쪽만 보이고 잘린다. 12% 미만이면 숫자를 아래 줄에서만 읽게 한다. -->
              <div class="db-pc" :style="{ width: (100 - (profile.mobileShare || 0)) + '%' }">
                <template v-if="(100 - (profile.mobileShare || 0)) >= 12">
                  PC {{ 100 - (profile.mobileShare || 0) }}%
                </template>
              </div>
              <div class="db-mo" :style="{ width: (profile.mobileShare || 0) + '%' }">
                <template v-if="(profile.mobileShare || 0) >= 12">
                  모바일 {{ profile.mobileShare || 0 }}%
                </template>
              </div>
            </div>
            <div class="dc-rows">
              <div><span>PC 검색량 ({{ 100 - (profile.mobileShare || 0) }}%)</span><b>{{ profile.pcVolume.toLocaleString() }}</b></div>
              <div><span>모바일 검색량 ({{ profile.mobileShare || 0 }}%)</span><b>{{ profile.mobileVolume.toLocaleString() }}</b></div>
            </div>
            <p class="dc-note">{{ deviceAdvice }}</p>
          </div>

          <!-- 광고 지표 -->
          <div class="detail-card">
            <div class="dc-title">광고·클릭 지표</div>
            <div class="dc-rows three">
              <div><span>모바일 클릭률</span><b>{{ profile.mobileCtr === null ? "-" : profile.mobileCtr.toFixed(2) + "%" }}</b></div>
              <div><span>월평균 클릭수</span><b>{{ fmt(profile.totalClicks) }}</b></div>
              <div><span>광고 밀도</span><b>{{ profile.adDepth === null ? "-" : profile.adDepth.toFixed(1) }}</b></div>
              <div><span>1위 입찰가</span><b>{{ profile.firstPositionBid === null ? "-" : profile.firstPositionBid.toLocaleString() + "원" }}</b></div>
            </div>
            <p class="dc-note">{{ commercialAdvice }}</p>
          </div>
        </template>
      </div>

      <!-- ── 트렌드 ── -->
      <div v-show="activeTab === 'trend'" class="tab-panel">
        <div v-if="!profile" class="skeleton-card">불러오는 중…</div>
        <div v-else-if="!profile.datalabAvailable" class="notice notice-warn">
          {{ profile.datalabMessage || "검색 트렌드를 사용할 수 없습니다." }}
        </div>
        <template v-else-if="chartData">
          <!-- 기간 단위 토글 — 데이터랩은 일/주/월만 지원(시간 단위 없음) -->
          <div class="unit-toggle">
            <button v-for="u in units" :key="u.key"
                    class="ut-btn" :class="{ active: trendUnit === u.key }"
                    :disabled="trendLoading" @click="changeUnit(u.key)">{{ u.label }}</button>
            <span class="ut-note">네이버는 일·주·월 단위만 제공합니다 (시간 단위 없음)</span>
          </div>

          <div class="summary-row">
            <div class="sum-item"><span>최고</span><b>{{ trendSummary.maxLabel }}</b></div>
            <div class="sum-item"><span>최저</span><b>{{ trendSummary.minLabel }}</b></div>
            <div class="sum-item"><span>최근 흐름</span>
              <b :class="trendSummary.dirClass">{{ trendSummary.direction }}</b></div>
          </div>

          <div v-if="trendSummary.surge" class="notice notice-warn">
            <b>지금 급등 중입니다</b> — 진행 중인 구간을 한 구간치로 환산하면
            직전 3구간 평균의 <b>{{ trendSummary.changeText }}</b>입니다.
            방송·이슈로 갑자기 튄 것일 수 있습니다.
            <b>월간 검색량 숫자 자체도 이 급등이 끌어올린 값</b>이라, 며칠 뒤면 원래대로 내려갈 수 있습니다.
            일별 탭에서 <b>언제 튀었는지</b> 확인해 보세요 — 하루만 튀었다면 짧게 소비되는 주제입니다.
          </div>
          <div v-else-if="trendSummary.hasPartial" class="notice notice-muted">
            마지막 구간({{ trendSummary.partialLabel }})은 <b>아직 진행 중</b>이라 막대가 낮게 보입니다.
            최고·최저에서는 제외했고, <b>최근 흐름은 한 구간치로 환산해</b> 비교했습니다.
          </div>

          <div class="chart-card">
            <p class="chart-note">
              데이터랩 기준 <b>상대비율</b>입니다(구간 최고점 = 100). 절대 검색량이 아닙니다.
            </p>
            <div class="chart-area"><Line :data="chartData" :options="chartOptions" class="chart" /></div>
          </div>

          <div class="detail-card">
            <div class="dc-title">구간별 수치</div>
            <div class="month-grid" :class="{ dense: trendUnit !== 'month' }">
              <div v-for="(p, i) in trendPoints" :key="p.period" class="mg-item"
                   :class="{ peak: p.ratio === trendSummary.max, partial: trendSummary.hasPartial && i === trendPoints.length - 1 }">
                <div class="mg-label">{{ periodLabel(p.period) }}</div>
                <div class="mg-value">{{ p.ratio.toFixed(0) }}</div>
              </div>
            </div>
            <p class="dc-note">{{ trendSummary.advice }}</p>
          </div>
        </template>
        <div v-else class="empty-state">추이 데이터가 없습니다.</div>
      </div>

      <!-- ── 성향 ── -->
      <div v-show="activeTab === 'insight'" class="tab-panel">
        <div v-if="insightLoading" class="skeleton-card">성향·요일 분석 불러오는 중…</div>
        <div v-else-if="insight && !insight.available" class="notice notice-warn">
          {{ insight.message || "성향 분석을 사용할 수 없습니다." }}
        </div>
        <template v-else-if="insight">
          <div v-if="insight.weekday && insight.weekday.length" class="weekday-card">
            <div class="wc-title">요일별 검색 비율</div>
            <div class="wc-bars">
              <div v-for="w in insight.weekday" :key="w.label" class="wc-bar">
                <div class="wc-track">
                  <div class="wc-fill" :class="{ peak: w.peak }" :style="{ height: barHeight(w.ratio) }"></div>
                </div>
                <div class="wc-label" :class="{ peak: w.peak }">{{ w.label }}</div>
                <div class="wc-value">{{ w.ratio.toFixed(0) }}</div>
              </div>
            </div>
            <div class="summary-row tight">
              <div class="sum-item"><span>가장 많이 찾는 날</span><b>{{ weekdaySummary.peak }}</b></div>
              <div class="sum-item"><span>가장 적은 날</span><b>{{ weekdaySummary.low }}</b></div>
              <div class="sum-item"><span>주말 vs 주중</span><b>{{ weekdaySummary.weekend }}</b></div>
            </div>
            <p class="wc-note">
              최근 8주 일별 데이터를 요일로 묶은 평균입니다. <b>요일끼리 비교가 유효</b>합니다 —
              {{ weekdaySummary.advice }}
            </p>
          </div>

          <div class="segment-card">
            <div class="wc-title">성별 · 연령대 추이</div>
            <p class="wc-note warn">
              ⚠️ 네이버가 <b>세그먼트마다 자기 최고점을 100으로</b> 정규화해서 주기 때문에
              "남성이 여성보다 많다" 같은 <b>크기 비교는 할 수 없습니다.</b>
              언제 오르내리는지 <b>추이 모양</b>만 비교하세요.
            </p>
            <div class="chart-area sm">
              <Line v-if="segmentChart" :data="segmentChart" :options="segmentOptions" class="chart" />
              <div v-else class="chart-skeleton">세그먼트 데이터가 없습니다.</div>
            </div>
          </div>
        </template>
      </div>

      <!-- ── 연관 키워드 ── -->
      <div v-show="activeTab === 'related'" class="tab-panel">
        <!-- 15~25초가 걸리는 작업이라 "멈춘 건지 도는 건지" 가 보여야 한다.
             남은 시간을 정확히 알 수는 없으므로(네이버가 진행률을 주지 않는다)
             진행 막대는 흐르게만 두고, 대신 경과 시간과 지금 하는 일을 적는다. -->
        <div v-if="relatedLoading" class="loading-panel">
          <div class="lp-bar"><i></i></div>
          <div class="lp-stage">{{ loadingStage }}</div>
          <div class="lp-meta">
            <span class="lp-elapsed">{{ relatedElapsed }}초 경과</span>
            <span class="lp-hint">보통 15~25초 걸립니다</span>
          </div>
          <ul class="lp-steps">
            <li :class="{ done: relatedElapsed >= 3, now: relatedElapsed < 3 }">연관 키워드 모으기</li>
            <li :class="{ done: relatedElapsed >= 8, now: relatedElapsed >= 3 && relatedElapsed < 8 }">후보 검색량 확인</li>
            <li :class="{ now: relatedElapsed >= 8 }">발행량 세기 (한 건씩)</li>
          </ul>
          <p class="lp-note">
            네이버 호출 제한 때문에 발행량은 <b>한 건씩 순차로</b> 조회합니다.
            한 번 조회한 키워드는 하루 동안 기억해 두므로 다시 찾을 때는 빠릅니다.
          </p>
        </div>
        <template v-else-if="result">
          <div class="scope-bar">
            <button
              v-for="s in scopes" :key="s.key"
              class="scope" :class="{ active: scope === s.key, empty: s.count === 0 }"
              :disabled="s.count === 0"
              @click="scope = s.key"
            >{{ s.label }} <b>{{ s.count }}</b></button>
          </div>

          <div class="result-summary">
            <span class="chip">연관 <b>{{ result.totalRelated.toLocaleString() }}</b></span>
            <span class="chip">분석 <b>{{ result.analyzed.toLocaleString() }}</b></span>
            <span class="chip good" v-if="satBuckets.good">추천 <b>{{ satBuckets.good }}</b></span>
            <button class="btn-csv" @click="downloadCsv">CSV</button>
          </div>

          <div class="notice notice-muted">
            <template v-if="scope === 'direct'">
              <b>{{ searchedKeyword }}</b>를 그대로 품은 키워드만 보고 있습니다.
              글감을 좁힐 때 쓰세요. 주변 주제까지 넓히려면 위에서 <b>전체</b>를 누르세요.
            </template>
            <template v-else>
              검색광고의 연관 키워드는 <b>뜻이 비슷한 말</b>이 아니라
              <b>같은 광고주가 함께 사는 키워드</b>입니다. 그래서 「라이코펜」에 「델몬트」처럼
              글자가 하나도 안 겹치는 것이 섞입니다(같은 업종 광고주가 함께 구매해서).
              <b>씨앗 포함</b>을 누르면 좁혀서 볼 수 있습니다.
            </template>
          </div>

          <div v-if="!result.docCountAvailable" class="notice notice-warn">
            네이버 오픈API 자격증명이 없어 발행량·포화도를 계산하지 못했습니다.
          </div>
          <div v-else-if="result.docCountFailed > 0" class="notice notice-warn">
            {{ result.docCountFailed }}개 키워드는 네이버 호출 제한으로 발행량을 가져오지 못했습니다.
          </div>

          <div class="table-scroll">
            <table class="keyword-table">
              <thead>
                <tr>
                  <th class="col-kw">키워드</th>
                  <th class="sortable" @click="sortBy('totalVolume')">검색량<i v-if="sortKey==='totalVolume'">{{ sortAsc?"▲":"▼" }}</i></th>
                  <th class="col-num col-device">PC</th>
                  <th class="col-num col-device">모바일</th>
                  <th class="sortable" @click="sortBy('docCount')">발행량<i v-if="sortKey==='docCount'">{{ sortAsc?"▲":"▼" }}</i></th>
                  <th class="sortable" @click="sortBy('saturation')">포화도<i v-if="sortKey==='saturation'">{{ sortAsc?"▲":"▼" }}</i></th>
                  <th class="col-comp">경쟁</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in sortedRows" :key="row.keyword">
                  <td class="col-kw">
                    <a href="#" @click.prevent="searchKeyword(row.keyword)">{{ row.keyword }}</a>
                    <i v-if="scope !== 'direct' && row.relevance === 'direct'" class="rel-dot" title="씨앗을 그대로 품은 키워드"></i>
                  </td>
                  <td class="col-num strong">{{ row.totalVolume.toLocaleString() }}</td>
                  <td class="col-num dim col-device">{{ row.pcVolume.toLocaleString() }}</td>
                  <td class="col-num dim col-device">{{ row.mobileVolume.toLocaleString() }}</td>
                  <td class="col-num">{{ fmt(row.docCount) }}</td>
                  <td class="col-num">
                    <span v-if="row.saturation === null">-</span>
                    <span v-else class="saturation" :class="saturationClass(row.saturation)">{{ row.saturation.toFixed(2) }}</span>
                  </td>
                  <td class="col-comp">{{ row.competition || "-" }}</td>
                </tr>
                <tr v-if="sortedRows.length === 0"><td colspan="7" class="empty">조건에 맞는 키워드가 없습니다.</td></tr>
              </tbody>
            </table>
          </div>
          <p class="table-note">
            키워드를 누르면 그 키워드로 다시 분석합니다.
            <span class="legend"><i class="dot good"></i>포화도 1 미만 {{ satBuckets.good }}개</span>
            <span class="legend"><i class="dot mid"></i>1~5 {{ satBuckets.mid }}개</span>
            <span class="legend"><i class="dot bad"></i>5 이상 {{ satBuckets.bad }}개</span>
          </p>
        </template>
      </div>

      <!-- ── 상위 블로그 ── -->
      <div v-show="activeTab === 'blogs'" class="tab-panel">
        <div v-if="profileLoading" class="skeleton-card">불러오는 중…</div>
        <template v-else-if="profile && profile.topBlogs && profile.topBlogs.length">
          <div class="notice notice-muted">
            <b>최근 발행된 네이버 블로그 글</b>입니다(최신순). 상위 노출 순위가 아닙니다.
            네이버는 <b>검색 결과 순위를 주는 공식 API가 없고</b>, 크롤링은 하지 않는다는 원칙이라
            순위를 재현할 방법이 없습니다.
            대신 <b>경쟁 글이 얼마나 자주 올라오는지</b>, 어떤 제목으로 쓰는지 보는 용도로 쓰세요.
          </div>
          <ul class="blog-list">
            <li v-for="(b, i) in profile.topBlogs" :key="b.link" class="blog-item">
              <span class="bl-no">{{ i + 1 }}</span>
              <div class="bl-body">
                <a class="bl-title" :href="b.link" target="_blank" rel="noopener">{{ b.title }}</a>
                <p class="bl-desc">{{ b.description }}</p>
                <div class="bl-meta">
                  <span class="bl-blogger">{{ b.blogger }}</span>
                  <span class="bl-date" :class="{ fresh: isFresh(b.postDate) }">{{ b.postDate }}</span>
                </div>
              </div>
            </li>
          </ul>
          <p class="table-note">{{ freshnessSummary }}</p>
        </template>
        <div v-else class="empty-state">표시할 블로그 글이 없습니다.</div>
      </div>

      <!-- ── 지표 읽는 법 ── -->
      <div v-show="activeTab === 'section'" class="tab-panel">
        <div class="detail-card">
          <div class="dc-title">각 숫자를 어떻게 읽나</div>
          <dl class="guide-dl">
            <dt>포화 지수 = 발행량 ÷ 검색량</dt>
            <dd>
              <b>1 미만</b> 글이 적어 노려볼 만함 · <b>1~5</b> 보통, 내용으로 승부 ·
              <b>5 이상</b> 이미 포화. 이 서비스가 기본으로 포화도 낮은 순 정렬하는 이유입니다.
            </dd>
            <dt>모바일 클릭률</dt>
            <dd>검색은 많은데 이 값이 낮으면 <b>검색 결과만 보고 이탈</b>하는 키워드입니다. 글을 써도 유입이 적습니다.</dd>
            <dt>광고 밀도</dt>
            <dd>높을수록 검색 결과 위쪽을 광고가 차지해 <b>자연검색 자리가 밀립니다.</b></dd>
            <dt>1위 입찰가</dt>
            <dd>광고비를 내고서라도 데려갈 만한 키워드인지 = <b>상업적 가치</b>입니다. 수익형 글감을 고를 때 봅니다.</dd>
            <dt>트렌드 · 성향의 상대비율</dt>
            <dd>
              데이터랩은 <b>구간 최고점을 100으로</b> 맞춰 줍니다. 절대 검색량이 아닙니다.
              특히 성별·연령은 세그먼트마다 따로 정규화돼 <b>서로 크기 비교가 불가능</b>합니다(모양만 비교).
            </dd>
          </dl>
        </div>

        <div class="detail-card">
          <div class="dc-title">이 서비스가 제공하지 못하는 것</div>
          <dl class="guide-dl">
            <dt>검색 결과 상위 노출 순위</dt>
            <dd>공식 API가 없습니다. 크롤링으로 우회하지 않는다는 원칙이라 제공하지 않습니다.</dd>
            <dt>스마트블록 · 섹션 배치</dt>
            <dd>위와 같은 이유로 불가능합니다.</dd>
            <dt>실시간 인기 검색어</dt>
            <dd>
              네이버는 2021년 폐지 후 부활 계획이 없습니다.
              (다음은 2026년 3월 부활 · 네이트 제공 중 · 구글 트렌드는 연예·스포츠 위주)
              데이터랩 인기검색어 TOP500 도 웹 화면 전용이라 API로 받을 수 없습니다.
              대신 <b>인기 키워드</b> 메뉴에서 검색광고 데이터로 만든 대체 랭킹을 제공합니다.
            </dd>
          </dl>
        </div>
      </div>
    </template>

    <div v-else-if="!anyLoading && !errorMessage" class="empty-state">
      키워드를 입력하고 <b>찾기</b>를 눌러 보세요.
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS, Title, Tooltip, Legend, LineElement,
  CategoryScale, LinearScale, PointElement, Filler,
} from "chart.js";
import type { ChartData, ChartOptions } from "chart.js";
import * as keywordApi from "@/keyword/api";
import type { KeywordRow, KeywordSearchResult, KeywordProfile, KeywordInsight } from "@/keyword/api";
import * as history from "@/keyword/utils/history";
import type { HistoryEntry } from "@/keyword/utils/history";

ChartJS.register(Title, Tooltip, Legend, LineElement, CategoryScale, LinearScale, PointElement, Filler);

const SEGMENT_COLORS = ["#8b7cf6", "#e763b4", "#4a9fe0", "#47c974", "#eab13a", "#ef5350", "#7f8fa6"];

export default defineComponent({
  name: "KeywordSearchView",
  components: { Line },
  data() {
    return {
      form: { seed: "", minVolume: null as number | null, maxVolume: null as number | null, limit: null as number | null },
      filterOpen: false,
      activeTab: "basic" as "basic" | "trend" | "insight" | "related" | "blogs" | "section",
      profileLoading: false,
      relatedLoading: false,
      insightLoading: false,
      errorMessage: "",
      profile: null as KeywordProfile | null,
      insight: null as KeywordInsight | null,
      result: null as KeywordSearchResult | null,
      sortKey: "saturation" as "totalVolume" | "docCount" | "saturation",
      sortAsc: true,
      // 트렌드 기간 단위 (데이터랩은 일·주·월만 지원)
      trendUnit: "month" as "date" | "week" | "month",
      trendOverride: null as { period: string; ratio: number }[] | null,
      trendLoading: false,
      units: [
        { key: "month" as const, label: "월별" },
        { key: "week" as const, label: "주별" },
        { key: "date" as const, label: "일별" },
      ],
      // 연관 키워드 표에서 볼 범위. 씨앗을 품은 키워드가 넉넉하면 조회 후 자동으로 direct 로 좁힌다.
      scope: "all" as "direct" | "partial" | "all",
      // 실제로 조회한 키워드. 입력창(form.seed)과 분리해야
      // "입력만 바꾸고 조회 안 한 상태"를 구분할 수 있다.
      searchedKeyword: "",
      // 연관 키워드 조회 경과 시간(초). 오래 걸리는 작업이라 진행 중임을 보여 준다.
      relatedElapsed: 0,
      relatedTimer: null as number | null,
      // 최근 조회 이력 (브라우저 보관, 최대 10건 — history.ts 주석 참고)
      history: [] as HistoryEntry[],
      activeHistoryId: null as string | null,
      // 진행 중인 조회를 저장소에서 지켜보는 타이머
      pendingTimer: null as number | null,
    };
  },
  computed: {
    anyLoading(): boolean {
      return this.profileLoading || this.relatedLoading || this.insightLoading;
    },
    /** 지금 무엇을 하고 있는지 — 경과 시간으로 짐작한 것이라 단정하지 않는다 */
    loadingStage(): string {
      if (this.relatedElapsed < 3) return "연관 키워드를 모으는 중입니다";
      if (this.relatedElapsed < 8) return "후보 키워드의 검색량을 확인하는 중입니다";
      if (this.relatedElapsed < 25) return "키워드별 블로그 발행량을 세는 중입니다";
      return "거의 다 됐습니다. 네이버 응답이 느린 것 같습니다";
    },
    /** 입력창을 고쳤지만 아직 조회하지 않은 상태 */
    isDirty(): boolean {
      return !!this.searchedKeyword && this.form.seed.trim() !== this.searchedKeyword;
    },
    tabs(): { key: string; label: string; badge?: string }[] {
      return [
        { key: "basic", label: "기본정보" },
        { key: "trend", label: "트렌드" },
        { key: "insight", label: "성향" },
        { key: "related", label: "연관 키워드", badge: this.result ? String(this.sortedRows.length) : undefined },
        { key: "blogs", label: "경쟁 글" },
        { key: "section", label: "지표 읽는 법" },
      ];
    },
    /** 상위 글의 최신성 — 최근 글이 많으면 계속 갱신되는 주제 */
    freshnessSummary(): string {
      const posts = this.profile?.topBlogs || [];
      if (posts.length === 0) return "";
      const fresh = posts.filter((b) => this.isFresh(b.postDate)).length;
      if (fresh >= posts.length * 0.7) {
        return `최근 6개월 글이 ${fresh}/${posts.length}건입니다. 계속 새 글이 올라오는 주제라 경쟁이 활발합니다.`;
      }
      if (fresh === 0) {
        return "상위 글이 모두 오래되었습니다. 최신 정보로 새로 쓰면 밀어낼 여지가 있습니다.";
      }
      return `최근 6개월 글이 ${fresh}/${posts.length}건입니다. 오래된 글이 섞여 있어 파고들 틈이 있습니다.`;
    },
    /** 검색량 규모 등급 */
    volumeGrade(): string {
      const v = this.profile?.totalVolume ?? 0;
      if (v >= 100000) return "대형 키워드 (10만↑)";
      if (v >= 10000) return "중형 키워드 (1만~10만)";
      if (v >= 1000) return "소형 키워드 (1천~1만)";
      return "틈새 키워드 (1천 미만)";
    },
    saturationText(): string {
      const s = this.profile?.saturation;
      if (s === null || s === undefined) return "발행량 미조회";
      if (s < 1) return "경쟁 여유 — 노려볼 만함";
      if (s < 5) return "보통 — 콘텐츠 질로 승부";
      return "포화 — 상위 노출 어려움";
    },
    deviceAdvice(): string {
      const m = this.profile?.mobileShare ?? 0;
      if (m >= 85) return "모바일이 압도적입니다. 짧은 문단, 세로 사진, 요약 먼저 배치가 유리합니다.";
      if (m >= 60) return "모바일 중심입니다. 스크롤로 읽기 편하게 구성하세요.";
      return "PC 비중이 높습니다. 표·긴 설명 같은 정보형 구성이 통합니다.";
    },
    commercialAdvice(): string {
      const bid = this.profile?.firstPositionBid ?? 0;
      const ctr = this.profile?.mobileCtr ?? 0;
      const parts: string[] = [];
      if (bid >= 2000) parts.push("입찰가가 높아 구매 의도가 강한 키워드입니다 — 제휴·수익형 글감으로 좋습니다.");
      else if (bid > 0) parts.push("입찰가가 낮아 상업적 경쟁은 덜합니다.");
      if (ctr > 0 && ctr < 0.3) parts.push("클릭률이 낮아 검색만 하고 이탈하는 비중이 큽니다 — 제목에서 답을 다 주지 마세요.");
      return parts.join(" ") || "추가 해석할 지표가 부족합니다.";
    },
    /** 현재 보고 있는 추이 데이터 (단위 전환 시 별도 로드분을 우선) */
    trendPoints(): { period: string; ratio: number }[] {
      return this.trendOverride ?? this.profile?.trend ?? [];
    },
    /**
     * 추이 요약.
     *
     * ⚠️ 마지막 구간은 **아직 진행 중**이라 값이 낮게 나온다(예: 9월 7일이면 9월은 7일치뿐).
     *    그대로 두면 "9월이 최저"라는 틀린 정보가 되므로 최고·최저 계산에서 제외한다.
     * ⚠️ 라벨에 연도를 함께 넣는다. 월만 쓰면 2025-09 와 2026-09 가 똑같이 "09월"이 된다.
     */
    trendSummary(): any {
      const t = this.trendPoints;
      if (t.length === 0) {
        return { maxLabel: "-", minLabel: "-", direction: "-", dirClass: "", max: 0, advice: "", hasPartial: false };
      }
      const hasPartial = t.length > 1 && this.isCurrentPeriod(t[t.length - 1].period);
      const solid = hasPartial ? t.slice(0, -1) : t;
      if (solid.length === 0) {
        return { maxLabel: "-", minLabel: "-", direction: "-", dirClass: "", max: 0, advice: "", hasPartial };
      }

      const max = Math.max(...solid.map((p) => p.ratio));
      const min = Math.min(...solid.map((p) => p.ratio));
      const maxP = solid.find((p) => p.ratio === max)!;
      const minP = solid.find((p) => p.ratio === min)!;

      // 🚨 "최근 흐름"은 진행 중 구간을 빼면 안 된다.
      // 예전에는 solid(=완료된 구간)만 보고 계산했다. 그 결과 라이코펜처럼
      // **이번 달에 급등한** 키워드가 월별에서는 "하락"으로 나왔다
      // (급등이 일어난 9월을 진행 중이라고 빼버려서 6→7→8월만 봤다).
      // 주별·일별은 급등 구간이 포함돼 "상승"으로 나와 서로 어긋났다.
      //
      // 그렇다고 진행 중 구간을 그대로 쓰면 매달 1~7일에는 모든 키워드가 "하락"이 된다.
      // 그래서 **일할 환산**한다 — 7일치 값이면 한 달치로 늘려 비교한다.
      const partial = hasPartial ? t[t.length - 1] : null;
      const elapsed = partial ? this.elapsedRatio(partial.period) : 0;
      const projected = partial && elapsed >= 0.1 ? partial.ratio / elapsed : null;

      const recentVals = solid.slice(-3).map((p) => p.ratio);
      const prevAvg = recentVals.length
        ? recentVals.reduce((s, v) => s + v, 0) / recentVals.length
        : 0;
      const latest = projected !== null ? projected : recentVals[recentVals.length - 1] ?? 0;
      const change = prevAvg > 0 ? latest / prevAvg : 1;

      const direction =
        change >= 2 ? "급등" : change > 1.25 ? "상승" : change < 0.8 ? "하락" : "보합";

      const unitName = this.trendUnit === "month" ? "달" : this.trendUnit === "week" ? "주" : "날";
      return {
        max,
        maxLabel: this.periodLabel(maxP.period, true),
        minLabel: this.periodLabel(minP.period, true),
        partialLabel: hasPartial ? this.periodLabel(t[t.length - 1].period, true) : "",
        hasPartial,
        direction,
        surge: direction === "급등",
        changeText: prevAvg > 0 ? change.toFixed(1) + "배" : "",
        dirClass: direction === "급등" ? "surge" : direction === "상승" ? "up" : direction === "하락" ? "down" : "",
        advice:
          this.trendUnit === "month"
            ? `검색이 가장 몰리는 시기는 ${this.periodLabel(maxP.period, true)}입니다. 그보다 2~4주 앞서 발행하면 상위 노출이 자리 잡은 상태로 성수기를 맞습니다.`
            : `가장 많이 찾은 ${unitName}은 ${this.periodLabel(maxP.period, true)}입니다.`,
      };
    },
    weekdaySummary(): any {
      const w = this.insight?.weekday || [];
      if (w.length === 0) return { peak: "-", low: "-", weekend: "-", advice: "" };
      const peak = w.reduce((a, b) => (a.ratio >= b.ratio ? a : b));
      const low = w.reduce((a, b) => (a.ratio <= b.ratio ? a : b));
      const wk = w.filter((x) => x.label === "토" || x.label === "일");
      const wd = w.filter((x) => x.label !== "토" && x.label !== "일");
      const avg = (arr: any[]) => (arr.length ? arr.reduce((s, x) => s + x.ratio, 0) / arr.length : 0);
      const weekendHigher = avg(wk) > avg(wd);
      return {
        peak: peak.label + "요일",
        low: low.label + "요일",
        weekend: weekendHigher ? "주말이 높음" : "주중이 높음",
        advice: `${peak.label}요일에 가장 많이 찾습니다. 하루 이틀 앞서 발행해 두면 유리합니다.`,
      };
    },
    satBuckets(): { good: number; mid: number; bad: number } {
      // 지금 표에 보이는 행 기준으로 센다. 전체 40개 기준으로 세면
      // "씨앗 포함 7개"만 보고 있는데 범례가 14개라고 나와 안 맞는다.
      const rows = this.sortedRows;
      let good = 0, mid = 0, bad = 0;
      rows.forEach((r) => {
        if (r.saturation === null) return;
        if (r.saturation < 1) good++;
        else if (r.saturation < 5) mid++;
        else bad++;
      });
      return { good, mid, bad };
    },
    /** 표 위 범위 선택 칩. 개수가 0인 칩은 눌리지 않는다. */
    scopes(): { key: "direct" | "partial" | "all"; label: string; count: number }[] {
      const r = this.result;
      if (!r) return [];
      const list: { key: "direct" | "partial" | "all"; label: string; count: number }[] = [
        { key: "direct", label: "씨앗 포함", count: r.directCount ?? 0 },
      ];
      // 부분 일치는 씨앗이 여러 낱말일 때만 생긴다. 없으면 칩 자체를 숨긴다.
      if ((r.partialCount ?? 0) > 0) {
        list.push({ key: "partial", label: "일부 겹침", count: r.partialCount });
      }
      list.push({ key: "all", label: "전체", count: r.rows.length });
      return list;
    },
    sortedRows(): KeywordRow[] {
      if (!this.result) return [];
      let rows = [...this.result.rows];
      // 검색광고 연관 키워드는 "광고 타겟팅 관점"이라 글자가 하나도 안 겹치는 것이 섞인다.
      // 연관도는 서버가 판정해서 내려준다(KeywordRelevance) — 화면에서 문자열을 다시 자르지 않는다.
      if (this.scope === "direct") {
        rows = rows.filter((r) => r.relevance === "direct");
      } else if (this.scope === "partial") {
        rows = rows.filter((r) => r.relevance === "partial" || r.relevance === "direct");
      }
      const key = this.sortKey;
      const dir = this.sortAsc ? 1 : -1;
      return rows.sort((a, b) => {
        const av = a[key]; const bv = b[key];
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return (av - bv) * dir;
      });
    },
    chartData(): ChartData<"line"> | null {
      const t = this.trendPoints;
      if (!t.length) return null;
      return {
        labels: t.map((p) => this.periodLabel(p.period)),
        datasets: [{
          label: "검색 추이",
          data: t.map((p) => p.ratio),
          borderColor: "#8b7cf6", backgroundColor: "rgba(139,124,246,0.15)",
          fill: true, tension: 0.4,
          pointRadius: this.trendUnit === "date" ? 1 : 3,
        }],
      };
    },
    chartOptions(): ChartOptions<"line"> {
      return { responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, max: 100 } } };
    },
    segmentChart(): ChartData<"line"> | null {
      if (!this.insight) return null;
      const segs = [...(this.insight.gender || []), ...(this.insight.age || [])]
        .filter((s) => s.points && s.points.length > 0);
      if (segs.length === 0) return null;
      return {
        labels: segs[0].points.map((p) => p.period.substring(0, 7)),
        datasets: segs.map((s, i) => ({
          label: s.label, data: s.points.map((p) => p.ratio),
          borderColor: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
          backgroundColor: "transparent", fill: false, tension: 0.4, pointRadius: 2, borderWidth: 2,
        })),
      };
    },
    segmentOptions(): ChartOptions<"line"> {
      return { responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: true, position: "bottom" } }, scales: { y: { beginAtZero: true, max: 100 } } };
    },
    /** 종합 판정 — 지표 조합으로 "쓸까 말까"를 한 줄로 */
    verdict(): { tone: string; title: string; desc: string } {
      const p = this.profile;
      if (!p) return { tone: "", title: "", desc: "" };
      const s = p.saturation;
      const v = p.totalVolume;
      if (s === null) {
        return { tone: "mid", title: "발행량을 확인하지 못했습니다",
          desc: "검색량만으로는 경쟁 여부를 판단할 수 없습니다. 잠시 후 다시 조회해 보세요." };
      }
      if (s < 1 && v >= 1000) {
        return { tone: "good", title: "좋은 글감입니다",
          desc: `검색은 월 ${v.toLocaleString()}회인데 글은 적습니다. 지금 쓰면 노출 가능성이 높습니다.` };
      }
      if (s < 5 && v >= 1000) {
        return { tone: "mid", title: "해볼 만합니다",
          desc: "경쟁이 보통입니다. 사진·구성·정보량으로 차별화하면 자리를 잡을 수 있습니다." };
      }
      if (v < 1000) {
        return { tone: "mid", title: "검색량이 적습니다",
          desc: "포화도는 낮지만 찾는 사람 자체가 적습니다. 보조 키워드로 쓰는 편이 낫습니다." };
      }
      return { tone: "bad", title: "경쟁이 치열합니다",
        desc: "이미 글이 넘칩니다. 연관 키워드 탭에서 포화도가 낮은 세부 키워드를 찾아보세요." };
    },
  },
  beforeUnmount() {
    this.stopElapsed();   // 조회 중에 화면을 떠나면 타이머가 남는다
    this.stopWatchPending();
  },
  methods: {
    rel(at: number): string {
      return history.relTime(at);
    },
    reloadHistory() {
      this.history = history.list();
      this.activeHistoryId = history.getActive();
    },
    /** 저장해 둔 결과를 그대로 되살린다 — 네이버를 다시 부르지 않는다 */
    restore(id: string) {
      const e = history.find(id);
      if (!e) return;
      this.stopWatchPending();
      // 아직 조회가 도는 중이면 채워지는 것을 지켜본다.
      // 조회를 띄운 화면을 떠나도 요청 자체는 계속 돌아 저장소에 쌓이므로,
      // 돌아왔을 때 저장소만 다시 읽으면 이어서 볼 수 있다.
      if (e.pending) {
        this.watchPending(id);
      }
      this.profile = e.profile as KeywordProfile | null;
      this.insight = e.insight as KeywordInsight | null;
      this.result = e.result as KeywordSearchResult | null;
      this.searchedKeyword = e.seed;
      this.form.seed = e.seed;
      this.trendOverride = null;
      this.trendUnit = "month";
      this.errorMessage = "";
      this.activeTab = "basic";
      this.scope = (this.result?.directCount ?? 0) >= 3 ? "direct" : "all";
      history.setActive(id);
      this.activeHistoryId = id;
    },
    dropHistory(id: string) {
      this.history = history.remove(id);
      this.activeHistoryId = history.getActive();
      // 지금 보고 있던 걸 지웠으면 화면도 비운다 — 결과만 남아 있으면 어디서 온 건지 알 수 없다
      if (!this.history.some((h) => h.seed === this.searchedKeyword)) {
        this.resetView();
      }
    },
    clearHistory() {
      if (!confirm("최근 조회 이력과 저장된 결과를 모두 지웁니다.")) return;
      history.clear();
      this.history = [];
      this.activeHistoryId = null;
      this.resetView();
    },
    resetView() {
      this.profile = null;
      this.insight = null;
      this.result = null;
      this.searchedKeyword = "";
      this.trendOverride = null;
    },
    fmt(v: number | null): string { return v === null || v === undefined ? "-" : v.toLocaleString(); },
    /** 최근 6개월 이내 글인지 */
    isFresh(postDate: string): boolean {
      if (!postDate) return false;
      const d = new Date(postDate);
      if (isNaN(d.getTime())) return false;
      return Date.now() - d.getTime() < 180 * 24 * 3600 * 1000;
    },
    /** 구간 라벨. 월만 쓰면 2025-09 와 2026-09 가 구분이 안 된다. */
    periodLabel(period: string, withYear = false): string {
      const [y, m, d] = period.split("-");
      if (this.trendUnit === "month") {
        return withYear ? `${y.substring(2)}년 ${Number(m)}월` : `${y.substring(2)}.${m}`;
      }
      return withYear ? `${y.substring(2)}.${m}.${d}` : `${m}.${d}`;
    },
    /** 이 구간이 아직 진행 중인지 (마지막 구간이 낮게 나오는 원인) */
    /**
     * 진행 중 구간이 얼마나 지났는지 (0~1).
     * 데이터랩은 <b>어제까지</b>만 준다 — 오늘 몫을 세면 값이 낮게 나오므로 어제까지로 센다.
     */
    elapsedRatio(period: string): number {
      const now = new Date();
      const p = new Date(period);
      if (this.trendUnit === "month") {
        const daysInMonth = new Date(p.getFullYear(), p.getMonth() + 1, 0).getDate();
        const done = Math.max(1, now.getDate() - 1);
        return Math.min(1, done / daysInMonth);
      }
      if (this.trendUnit === "week") {
        const done = Math.max(1, Math.floor((now.getTime() - p.getTime()) / 86400000));
        return Math.min(1, done / 7);
      }
      return 1;   // 일별은 나눌 것이 없다
    },
    isCurrentPeriod(period: string): boolean {
      const now = new Date();
      const p = new Date(period);
      if (this.trendUnit === "month") {
        return p.getFullYear() === now.getFullYear() && p.getMonth() === now.getMonth();
      }
      if (this.trendUnit === "week") {
        return now.getTime() - p.getTime() < 7 * 24 * 3600 * 1000;
      }
      return p.toDateString() === now.toDateString();
    },
    async changeUnit(unit: "date" | "week" | "month") {
      if (this.trendUnit === unit || !this.profile) return;
      this.trendUnit = unit;
      if (unit === "month") {
        this.trendOverride = null;   // 월별은 프로필에 이미 들어 있다
        return;
      }
      this.trendLoading = true;
      try {
        const res: any = await keywordApi.getTrend(this.profile.keyword, unit);
        const payload = res?.data;
        this.trendOverride = payload?.trend ?? [];
      } catch (e) {
        this.trendOverride = [];
      } finally {
        this.trendLoading = false;
      }
    },
    async doSearch() {
      const seed = this.form.seed.trim();
      if (!seed) { this.errorMessage = "키워드를 입력해 주세요."; return; }
      this.errorMessage = "";
      this.profile = null; this.insight = null; this.result = null;
      this.trendOverride = null; this.trendUnit = "month";
      this.activeTab = "basic";
      this.searchedKeyword = seed;   // 어떤 키워드의 결과인지 화면에 고정한다

      // 🚨 이력 자리를 <b>먼저</b> 잡는다.
      // 예전에는 셋이 다 끝난 뒤에 넣어서, 15~25초 동안 목록이 비어 있었고
      // 그 사이 다른 메뉴에 다녀오면 진행 중이던 조회가 통째로 날아갔다.
      const entry = history.begin(seed);
      this.reloadHistory();

      // 셋을 한꺼번에 띄우되, 각각 끝나는 대로 이력에 채워 넣는다.
      // 중간에 화면을 떠나도 거기까지 받은 것은 남는다.
      await Promise.allSettled([
        this.loadProfile(seed).then(() => this.stash(entry.id, { profile: this.profile })),
        this.loadInsight(seed).then(() => this.stash(entry.id, { insight: this.insight })),
        this.loadRelated(seed).then(() => this.stash(entry.id, { result: this.result })),
      ]);
      this.stash(entry.id, { pending: false, at: Date.now() });
    },
    /** 이력 항목에 받은 것만 채운다 */
    stash(id: string, fields: Record<string, unknown>) {
      history.update(id, fields);
      this.reloadHistory();
    },
    /**
     * 진행 중인 조회를 저장소에서 지켜본다.
     * 조회를 띄운 화면 인스턴스는 이미 사라졌어도 요청은 계속 돌아 저장소를 채운다.
     */
    watchPending(id: string) {
      let waited = 0;
      this.pendingTimer = window.setInterval(() => {
        waited += 2;
        const e = history.find(id);
        if (!e) { this.stopWatchPending(); return; }

        if (e.profile && !this.profile) this.profile = e.profile as KeywordProfile;
        if (e.insight && !this.insight) this.insight = e.insight as KeywordInsight;
        if (e.result && !this.result) {
          this.result = e.result as KeywordSearchResult;
          this.scope = (this.result.directCount ?? 0) >= 3 ? "direct" : "all";
        }
        // 60초면 어차피 끝났거나 실패한 것이다. 무한히 돌리지 않는다.
        if (!e.pending || waited >= 60) {
          this.reloadHistory();
          this.stopWatchPending();
        }
      }, 2000);
    },
    stopWatchPending() {
      if (this.pendingTimer !== null) {
        window.clearInterval(this.pendingTimer);
        this.pendingTimer = null;
      }
    },
    async loadProfile(seed: string) {
      this.profileLoading = true;
      try {
        // axiosRequest 는 BaseResponse 봉투를 그대로 준다 → payload 는 res.data
        const res: any = await keywordApi.getProfile(seed);
        const payload = res?.data;
        if (payload && payload.keyword) this.profile = payload;
        else if (res?.status === 403) this.errorMessage = "키워드 리서치 사용 권한이 없습니다.";
        else this.errorMessage = res?.message || payload?.message || "기본정보 조회에 실패했습니다.";
      } catch (e: any) {
        this.errorMessage = e?.message || "기본정보 조회 중 오류가 발생했습니다.";
      } finally { this.profileLoading = false; }
    },
    async loadInsight(seed: string) {
      this.insightLoading = true;
      try {
        const res: any = await keywordApi.getInsight(seed);
        const payload = res?.data;
        if (payload && typeof payload.available === "boolean") this.insight = payload;
      } catch (e) {
        this.insight = null;   // 성향 실패가 화면 전체를 막지 않게
      } finally { this.insightLoading = false; }
    },
    async loadRelated(seed: string) {
      this.relatedLoading = true;
      this.startElapsed();
      try {
        const res: any = await keywordApi.search({
          seed, minVolume: this.form.minVolume || null,
          maxVolume: this.form.maxVolume || null, limit: this.form.limit || null,
        });
        const payload = res?.data;
        if (payload && Array.isArray(payload.rows)) {
          this.result = payload;
          // 씨앗을 품은 키워드가 볼 만큼 있으면 그쪽을 먼저 보여준다.
          // 3개 미만이면 좁혀 봐야 표가 비어 보이므로 전체로 둔다.
          this.scope = (payload.directCount ?? 0) >= 3 ? "direct" : "all";
        } else if (!this.errorMessage) {
          this.errorMessage = res?.message || "연관 키워드 조회에 실패했습니다.";
        }
      } catch (e: any) {
        if (!this.errorMessage) this.errorMessage = e?.message || "연관 키워드 조회 중 오류가 발생했습니다.";
      } finally { this.relatedLoading = false; this.stopElapsed(); }
    },
    startElapsed() {
      this.stopElapsed();
      this.relatedElapsed = 0;
      this.relatedTimer = window.setInterval(() => { this.relatedElapsed += 1; }, 1000);
    },
    stopElapsed() {
      if (this.relatedTimer !== null) {
        window.clearInterval(this.relatedTimer);
        this.relatedTimer = null;
      }
    },
    searchKeyword(keyword: string) {
      this.form.seed = keyword;
      window.scrollTo({ top: 0, behavior: "smooth" });
      this.doSearch();
    },
    downloadCsv() {
      const rows = this.sortedRows;
      const header = "키워드,총검색량,PC,모바일,발행량,포화도,경쟁";
      const body = rows.map((r) =>
        [r.keyword, r.totalVolume, r.pcVolume, r.mobileVolume,
         r.docCount ?? "", r.saturation ?? "", r.competition ?? ""].join(",")).join("\n");
      // BOM 을 붙여야 엑셀에서 한글이 깨지지 않는다
      const blob = new Blob(["﻿" + header + "\n" + body], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `keyword_${this.form.seed}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    barHeight(ratio: number): string {
      const max = Math.max(...(this.insight?.weekday || []).map((w) => w.ratio), 1);
      return Math.max(4, Math.round((ratio / max) * 100)) + "%";
    },
    sortBy(key: "totalVolume" | "docCount" | "saturation") {
      if (this.sortKey === key) this.sortAsc = !this.sortAsc;
      else { this.sortKey = key; this.sortAsc = key !== "totalVolume"; }
    },
    saturationClass(value: number | null): string {
      if (value === null || value === undefined) return "";
      if (value < 1) return "good";
      if (value < 5) return "mid";
      return "bad";
    },
    runFromQuery() {
      const q = this.$route.query.q;
      const seed = typeof q === "string" ? q.trim() : "";
      if (!seed) return false;

      // 이미 다 받아 둔 말이면 다시 부르지 않는다 — 호출도 아깝고 기다릴 이유도 없다.
      // 조회가 덜 끝난(pending) 것은 되살려 봐야 반쪽이므로 다시 부른다.
      const cached = this.history.find((h) => h.seed === seed && !h.pending);
      if (cached) {
        this.restore(cached.id);
        return true;
      }
      this.form.seed = seed;
      this.doSearch();
      return true;
    },
  },
  watch: { "$route.query.q"() { this.runFromQuery(); } },
  created() {
    this.reloadHistory();
    // 주소에 q 가 있으면 그게 우선, 없으면 마지막으로 보던 결과를 되살린다.
    // 다른 메뉴에 다녀와도 화면이 비지 않게 하려는 것이다(사용자 요청).
    if (!this.runFromQuery() && this.activeHistoryId) {
      this.restore(this.activeHistoryId);
    }
  },
});
</script>

<style lang="scss" scoped></style>
